<template>
    <div :aria-labelledby="name" class="my-2" :class="containerClass" v-click-outside="() => hide()">
        <slot></slot>

        <div ref="select" :class="[status ? 'error' : '', lower ? 'upper' : 'lower', open ? 'open' : '']" class="select-wrapper relative rounded-md bg-blue-light/50">
            <span ref="selection" class="dark:text-white label h-full w-full relative flex items-center justify-between" @click="open = !open">
                <span v-if="multiple" class="flex items-center justify-between gap-2">
                    <span v-if="localSelected.length > 0" class="flex items-center gap-2 flex-wrap">
                        <div v-for="(option, index) in localSelected" :key="index" class="bg-blue/10 dark:text-white p-1 text-xs inline-flex gap-2">
                            {{ $translate(option.label) }}
                            <span class="text-blue rounded-md flex items-center border border-blue py-0.5 px-1 text-xs" @click.stop="[select(option), (this.open = !this.open)]">
                                <FontAwesomeIcon :icon="['fa', 'times']" />
                            </span>
                        </div>
                    </span>
                    <span v-else>
                        {{ $translate(label) }}
                    </span>
                </span>
                <span v-else>
                    <span v-if="localSelected.length">
                        {{ $translate(localSelected[0].label) }}
                    </span>
                    <span v-else>
                        {{ $translate(label) }}
                    </span>
                </span>
                <FontAwesomeIcon class="transition-all duration-150" :class="open ? 'rotate-180' : ''" :icon="['fa', 'chevron-down']" />
            </span>

            <transition name="section">
                <div
                    ref="dropdown"
                    v-show="open"
                    :class="lower ? 'rounded-t-none border-t-0' : 'rounded-b-none border-b-0'"
                    class="bg-white left-[-1px] right-[-1px] max-h-[12rem] h-auto absolute border border-gray-300 rounded-md shadow-md z-10 overflow-y-auto"
                >
                    <div class="relative">
                        <div v-if="searchable || endpoint" class="border-gray-300 flex flex-row gap-2 items-center p-2 border-b sticky top-0 left-0 right-0 bg-white">
                            <span class="text-blue/50 p-0.5 text-xs">
                                <FontAwesomeIcon :icon="['fa', 'search']" />
                            </span>
                            <input class="input" :placeholder="$t('plain_search')" v-model="search" />
                        </div>
                        <div
                            v-for="(option, index) in filteredOptions"
                            :key="index"
                            class="border-gray-200 p-2 border-b cursor-pointer flex flex-row items-center gap-2"
                            :class="isSelected(option) ? 'bg-blue/5' : ''"
                            @click="[select(option), (this.open = !this.open)]"
                        >
                            <span v-if="isSelected(option)" class="text-blue border border-blue rounded-md flex items-center p-0.5 text-xs">
                                <FontAwesomeIcon :icon="['fa', 'check']" />
                            </span>
                            <div class="w-full">
                                <p class="truncate">{{ $translate(option.label) }}</p>
                                <p v-if="option.sublabel" class="text-xs">
                                    {{ $translate(option.sublabel) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </div>

        <FormError :formComponentId="formComponentId" :name="name" v-model:status="status" />
    </div>
</template>

<script>
import api from '../../api';
import _ from 'lodash';

export default {
    props: {
        options: Array,
        selected: String,
        label: String,
        name: String,
        multiple: Boolean,
        searchable: Boolean,
        endpoint: String,
        containerClass: String,
        language: String,
        separators: Array,
        formComponentId: '',
        noValueAllowed: Boolean
    },

    emits: ['update:selected'],

    data: () => ({
        status: null,
        open: false,
        search: null,
        filteredOptions: null,
        localSelected: [],
        lower: false,
        resizeObserver: null,
        timeout: null
    }),

    mounted() {
        this.initSelected();
        this.position();

        this.filteredOptions = this.options;

        window.addEventListener('scroll', this.position);

        this.resizeObserver = new ResizeObserver(
            function (entries) {
                this.position();
            }.bind(this)
        );

        this.resizeObserver.observe(this.$refs.select);
    },

    beforeUnmount() {
        window.removeEventListener('scroll', this.position);

        this.resizeObserver.unobserve(this.$refs.select);
    },

    methods: {
        initSelected() {
            if (typeof this.selected === 'string' || this.selected instanceof String) {
                this.localSelected = this.options.filter((option) => option.value === this.selected);
            } else if (this.selected != null && this.selected.isArray()) {
                this.localSelected = this.options.filter((option) => this.selected.includes(option.value));
            } else {
                this.localSelected = this.options.filter((option) => this.selected);
            }

            // if no label then preselect the first item
            if (!this.label) {
                this.select(this.options[0]);
            }
        },

        isSelected(option) {
            return this.localSelected.includes(option);
        },

        select(option) {
            // only deselect item if empty selection is allowed
            if (!this.label && this.localSelected.includes(option) && this.localSelected.length <= 1) {
                return;
            }

            if (this.multiple) {
                if (this.localSelected.includes(option) && !this.label) {
                    // only delete if empty values are allowed
                    if (!this.noValueAllowed && this.localSelected.length > 1) {
                        this.localSelected.splice(this.localSelected.indexOf(option), 1);
                    }
                } else {
                    this.localSelected.push(option);
                }

                this.$emit('update:selected', _.map(this.localSelected, 'value'));
            } else {
                if (this.localSelected.includes(option)) {
                    // only delete if empty values are allowed
                    if (this.noValueAllowed) {
                        this.localSelected = [];
                    }
                } else {
                    this.localSelected = [option];
                }

                this.$emit('update:selected', _.map(this.localSelected, 'value')[0]);
            }
        },

        position() {
            let height = window.innerHeight;
            let element = this.$refs.select;
            let dropdown = this.$refs.dropdown;

            this.lower = element.getBoundingClientRect().top < height;

            element.getBoundingClientRect().top < height ? (dropdown.style.bottom = '') : (dropdown.style.bottom = element.clientHeight + 'px');
        },

        hide() {
            this.open = false;
        }
    },

    watch: {
        search() {
            // autocomplete search
            if (!_.isEmpty(this.search) && this.search.trim().length >= 3 && this.endpoint) {
                let params = {};
                params.search = this.search;

                if (this.language) params.language = this.language;

                api.get(this.endpoint, { params: params })
                    .then((response) => {
                        this.filteredOptions = response.data;
                    })
                    .catch((error) => {
                        this.filteredOptions = this.options;
                    });
            }
            // manual search without autocomplete
            else if (!_.isEmpty(this.search)) {
                this.filteredOptions = this.options.filter((option) => {
                    return this.$translate(option.label).toLowerCase().includes(this.search.toLowerCase());
                });
            }
            // return all options
            else {
                return this.options;
            }
        }
    }
};
</script>
